import { Asset, MetadataFieldType, MetadataValue } from '@/api/generatedApi';
import { useUpdateAsset } from '@/hooks/api/assets/useUpdateAsset';
import { useDeleteMetadataFromAsset } from '@/hooks/api/metadata/useDeleteMetadataFromAsset';
import { useToast } from '@/hooks/useToast';
import { Box } from '@mui/material';
import { useState } from 'react';
import { InputField } from '../../Inputs/InputField';
import { MetadataActions } from './MetadataActions';

interface MetadataEditorProperties {
  asset: Asset;
  fieldName: string;
  fieldType: MetadataFieldType;
  currentValue: MetadataValue | undefined;
  onSuccess?: (savedValue?: MetadataValue) => void;
  onCancel?: () => void;
}

export const MetadataEdit = ({
  asset,
  fieldName,
  fieldType,
  currentValue,
  onSuccess,
  onCancel,
}: MetadataEditorProperties) => {
  const { updateAsset, isUpdating } = useUpdateAsset();
  const { showToast } = useToast();
  const { mutate: deleteMetadata, isPending: isDeleting } = useDeleteMetadataFromAsset({
    onSuccess: () => {
      showToast({
        message: `Successfully deleted "${fieldName}" metadata`,
        type: 'success',
      });
      onSuccess?.();
    },
    onError: error => {
      showToast({
        message: `Failed to delete "${fieldName}" metadata: ${error}`,
        type: 'error',
      });
    },
  });

  const [value, setValue] = useState(currentValue);

  const handleSave = () => {
    if (!asset?.id) return;

    if (value === '' || value === null || value === undefined) {
      handleDelete();
      return;
    }

    onSuccess?.(value);
    updateAsset(
      {
        assetId: asset.id,
        data: {
          metadata: {
            [fieldName]: value,
          },
        },
      },
      {
        onError: () => {
          showToast({
            message: `Failed to update "${fieldName}" metadata`,
            type: 'error',
          });
        },
      }
    );
  };

  const handleDelete = () => {
    if (!asset?.id) return;

    deleteMetadata({
      assetId: asset.id,
      metadataFieldNames: [fieldName],
    });
  };

  const isValid = value !== currentValue;
  const isDeleteDisabled = currentValue === undefined || currentValue === null;

  return (
    <Box sx={{ p: 1, minWidth: '110px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <InputField fieldType={fieldType} value={value} onChange={setValue} autoFocus onEnter={handleSave} />
        <MetadataActions
          onSave={handleSave}
          onCancel={onCancel}
          onDelete={handleDelete}
          isSaveDisabled={!isValid}
          isDeleteDisabled={isDeleteDisabled}
          isLoading={isUpdating || isDeleting}
        />
      </Box>
    </Box>
  );
};
